import { useEffect, useState } from "react";
import { Users, UserCheck, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";

// Lane routing: every lane contact is notified; the first to respond owns it.
export function laneState(r, user) {
  const me = (user?.email || "").toLowerCase();
  const owner = (r.claimed_by_email || "").toLowerCase();
  const member = (r.lane_contacts || []).some((c) => c.email === me);
  const override = ["admin", "coordinator"].includes(user?.role);
  const isOwner = !!owner && owner === me;
  return { isOwner, blocked: !!owner && !isOwner && member && !override, canReassign: override || isOwner };
}

export default function OwnershipPanel({ referral: r, user, onChanged }) {
  const [directory, setDirectory] = useState([]);
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const st = laneState(r, user);
  const lane = r.lane_contacts || [];
  const open = r.aging?.is_open;
  const showControl = st.canReassign && open;

  useEffect(() => {
    if (!showControl) { return; }
    api.get("/contacts").then((res) => setDirectory(res.data.filter((c) => c.active && c.receiving_line === r.receiving_line
      && (!c.location || c.location === r.location)))).catch(() => setDirectory([]));
  }, [showControl, r.receiving_line, r.location]);

  if (!lane.length && !r.claimed_by_email) return null;
  const options = [...lane, ...directory.map((c) => ({ name: c.name, email: c.email.toLowerCase() }))]
    .filter((c, i, a) => a.findIndex((x) => x.email === c.email) === i && c.email !== r.claimed_by_email);

  const assign = async () => {
    setBusy(true);
    try {
      await api.post(`/referrals/${r.id}/assign`, { email: target === "__release" ? null : target });
      toast.success(target === "__release" ? "Released to the whole lane" : "Ownership reassigned");
      setTarget("");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reassign");
    } finally { setBusy(false); }
  };

  return (
    <div className="glass rounded-xl p-4 flex flex-wrap items-start justify-between gap-4" data-testid="ownership-panel">
      <div className="space-y-2 min-w-0">
        <h2 className="font-head font-bold text-sm uppercase tracking-wide text-cyan-300 flex items-center gap-2"><Users className="w-4 h-4" /> Lane ownership</h2>
        {r.claimed_by_email ? (
          <p className="text-sm flex items-center gap-2" data-testid="ownership-owner">
            <UserCheck className="w-4 h-4 text-emerald-400" /> Owned by <b>{r.claimed_by_name}</b>
            {r.claimed_at && <span className="text-xs font-mono text-muted-foreground">since {fmtDate(r.claimed_at)}</span>}
          </p>
        ) : (
          <p className="text-sm text-amber-300" data-testid="ownership-unclaimed">Unclaimed — sent to everyone in this lane. The first to respond takes ownership.</p>
        )}
        <div className="flex flex-wrap gap-1.5" data-testid="ownership-lane-list">
          {lane.map((c) => (
            <span key={c.email} title={c.email} className={`text-xs font-mono rounded px-2 py-0.5 border ${c.email === r.claimed_by_email ? "border-emerald-500/50 text-emerald-300" : "border-white/10 text-muted-foreground"}`}>{c.name}</span>
          ))}
        </div>
        {st.blocked && (
          <p className="text-xs text-amber-300 flex items-center gap-1.5" data-testid="ownership-blocked">
            <Lock className="w-3.5 h-3.5" /> {r.claimed_by_name} is handling this. Ask them, a coordinator or an admin to reassign it to you.
          </p>
        )}
      </div>
      {showControl && (
        <div className="flex items-center gap-2 flex-wrap">
          <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="ownership-assign-select"
                  className="h-9 rounded-md bg-slate-900/60 border border-white/10 px-2 text-sm">
            <option value="">{st.isOwner ? "Hand off to…" : "Reassign to…"}</option>
            {options.map((c) => <option key={c.email} value={c.email}>{c.name}</option>)}
            {r.claimed_by_email && <option value="__release">Release to the whole lane</option>}
          </select>
          <Button onClick={assign} disabled={busy || !target} data-testid="ownership-assign-btn" variant="outline" className="border-white/15 h-9">
            {busy ? "Saving…" : "Apply"}
          </Button>
        </div>
      )}
    </div>
  );
}
